import { useCallback } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Link from '@mui/material/Link'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { useTeam } from '../../contexts/TeamContext'
import { useNodes } from '../../contexts/NodesContext'
import useNodesResources from '../../shared/resources/api/v1/admin/useNodesResources'
import { useConfirmationDialog } from '../../system/ConfirmationDialogProvider'
import LoadingState from '../../shared/components/LoadingState'
import ErrorState from '../../shared/components/ErrorState'
import Page from '../../shared/components/layouts/Page'
import PageHeader from '../../shared/components/layouts/PageHeader'
import PageBody from '../../shared/components/layouts/PageBody'

const getNodeStatus = (node) => {
  if (!node.lastSeenAt) return 'unknown'
  const lastSeen = new Date(node.lastSeenAt)
  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000)
  return lastSeen > fiveMinutesAgo ? 'online' : 'offline'
}

const getStatusColor = (status) => {
  switch (status) {
    case 'online':
      return 'success'
    case 'offline':
      return 'error'
    default:
      return 'default'
  }
}

const formatUtilization = (value) => {
  if (value === undefined || value === null) return '-'
  return `${Math.round(value)}%`
}

function NodeIndexPage() {
  const { teamSlug } = useParams()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { team } = useTeam()
  const { nodes, loading, error, reload } = useNodes()
  const { destroy } = useNodesResources(teamSlug)
  const { openConfirmationDialog } = useConfirmationDialog()

  const handleDeleteNode = useCallback((node) => {
    openConfirmationDialog({
      title: <FormattedMessage id="nodes.delete.title" />,
      message: <FormattedMessage id="nodes.delete.message" />,
      onConfirm: () => destroy(node.id)
        .then(() => {
          reload()
        })
        .catch(() => {
          enqueueSnackbar(formatMessage({ id: 'nodes.delete.error' }), { variant: 'error' })
        }),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroy, reload, enqueueSnackbar, formatMessage])

  if (loading) return <LoadingState />
  if (error) return <ErrorState messageId="nodes.error" />

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="nodes.title" />}
        entities={{ team }}
        actions={
          <Button
            component={RouterLink}
            to={`/teams/${team.slug}/nodes/new`}
            variant="contained"
            startIcon={<AddIcon />}
          >
            <FormattedMessage id="nodes.actions.create" />
          </Button>
        }
      />
      <PageBody>
        {nodes.length === 0 ? (
          <Typography color="text.secondary">
            <FormattedMessage id="nodes.empty" />
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FormattedMessage id="nodes.index.columns.status" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="nodes.index.columns.name" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="nodes.index.columns.ip" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="nodes.index.columns.agentVersion" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="nodes.index.columns.cpu" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="nodes.index.columns.memory" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="nodes.index.columns.disk" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="nodes.index.columns.lastSeen" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="nodes.index.columns.actions" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nodes.map((node) => {
                  const status = getNodeStatus(node)
                  const latestStatus = node.latestStatus || {}

                  return (
                    <TableRow key={node.id}>
                      <TableCell>
                        <Chip
                          label={<FormattedMessage id={`nodes.status.${status}`} />}
                          color={getStatusColor(status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/teams/${team.slug}/nodes/${node.id}`}
                        >
                          {node.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {node.ip || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{latestStatus.agentVersion || '-'}</TableCell>
                      <TableCell>{formatUtilization(latestStatus.cpuUtilization)}</TableCell>
                      <TableCell>{formatUtilization(latestStatus.memoryUtilization)}</TableCell>
                      <TableCell>{formatUtilization(latestStatus.diskUtilization)}</TableCell>
                      <TableCell>
                        {node.lastSeenAt ? (
                          <FormattedDate
                            value={node.lastSeenAt}
                            year="numeric"
                            month="short"
                            day="numeric"
                            hour="numeric"
                            minute="numeric"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteNode(node)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageBody>
    </Page>
  )
}

export default NodeIndexPage
